import { createInstance, isBrowserSupported, SessionState } from "@fra.me/terminal-factory";
import type { BrowserOptions } from "@sentry/browser";
import * as Sentry from "@sentry/browser";
import axios from "axios";
import { version } from "../../package.json";
import data from "../assets/notices.json";
import {
  ApiEndpoints,
  AwsRegionNames,
  CookieNames,
  FrameAccountConfigs,
  StorageKeys,
} from "./config.js";
import { clearCookie, getCookie, setCookie } from "./cookies.js";
import type { LatencyResult } from "./je-latency.js";
import pingRegion from "./je-latency.js";
import type { NoticesJson } from "./notifications.js";
import { hideAwsNotification, showNotices } from "./notifications.js";
import type { SessionDependencies } from "./session.js";
import { checkUntilClosed, loadFrame, startSession } from "./session.js";
import { postScreenSpecs, setSentryLocation, setSentryUser } from "./telemetry.js";
import { getToken } from "./token.js";
import { toggleStartButton, updateYear } from "./ui.js";
import { clearLocalStorage, getEnumKeyByEnumValue } from "./utils.js";

/**
 * App
 * Copyright 2025 ENERCALC, LLC
 * All rights reserved.
 *
 * This file is used on the production site `index.html`.
 *
 * @author: ENERCALC, LLC
 * @version: 1.0.0
 */

// Terminal class is not exported from @fra.me/terminal-factory — derive type from createInstance.
type FrameTerminal = Awaited<ReturnType<typeof createInstance>>;

/** @type {BrowserOptions} */
const sentryOptions: BrowserOptions = {
  dsn: "https://c372ce87842a2c9d87d7ae05a6396094@o4509011001344000.ingest.us.sentry.io/4509555974733824",
  sendDefaultPii: true,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.captureConsoleIntegration({ levels: ["error"] }),
    Sentry.httpClientIntegration({
      failedRequestStatusCodes: [400, 401, 403, 404, 500, 502, 503, 504, 599],
      failedRequestTargets: ["https://enercalcapp.com/.*", "https://eneractivtest.com/.*"],
    }),
  ],
  tracesSampleRate: 0.5,
  tracePropagationTargets: [/^https:\/\/eneractivtest\.com/],
  environment: process.env.NODE_ENV,
  release: process.env.CODEBUILD_RESOLVED_SOURCE_VERSION || process.env.GIT_SHA || version,
};

Sentry.init(sentryOptions);

let terminal: FrameTerminal | null = null;
let selectedLocation = "";

/**
 * Returns the highest-priority (lowest latency) region whose value appears
 * as an `<option>` in the given Set of available region IDs.
 *
 * Regions present in AwsRegionNames but absent from the `<select>` (e.g. NORCAL,
 * SYDNEY) are excluded so the app cannot auto-select a region the user cannot
 * actually choose.
 *
 * @param sortedResults - Latency results sorted ASC by latency.
 * @param availableIds - Region value strings from the `<select>` options.
 * @returns The lowest-latency available region, or null if none found.
 */
export function pickLowestLatencyAvailableRegion(
  sortedResults: LatencyResult[],
  availableIds: Set<string>
): LatencyResult | null {
  const selectable = sortedResults.filter((r) => availableIds.has(r.id));
  return selectable.length > 0 ? selectable[0] : null;
}

document.addEventListener("DOMContentLoaded", async () => {
  const startButton = document.getElementById("btnlaunch") as HTMLButtonElement;
  const clearButton = document.getElementById("btnClear") as HTMLButtonElement;
  const statusMessage = document.getElementById("loginCheck") as HTMLElement;
  const kwInput = document.getElementById("kw_number") as HTMLInputElement;
  const kwPasswordInput = document.getElementById("kw_password") as HTMLInputElement;
  const userNameInput = document.getElementById("txt_username") as HTMLInputElement;
  const errorContainer = document.getElementById("LoginErrorMessage") as HTMLElement;
  const selectedLocationInput = document.querySelector("#vmlocation") as HTMLSelectElement;
  const loginForm = document.querySelector("#loginForm") as HTMLFormElement;
  const loginFieldset = document.querySelector("#loginFieldset") as HTMLFieldSetElement;
  const browserAdvisory = document.querySelector("#browserAdvisory") as HTMLElement;
  const copyrightYear = document.querySelector("#copyrightYear") as HTMLElement;
  const awsNotificationBanner = document.getElementById("aws-notification-banner") as HTMLElement;
  const awsNotificationCloseBtn = awsNotificationBanner?.querySelector(
    ".notification-banner__close"
  ) as HTMLElement | null;

  const kwnumber = getCookie(CookieNames.KWNUMBER);
  const userpass = getCookie(CookieNames.PASSWORD);
  const username = getCookie(CookieNames.USERNAME);

  /**
   * Handles errors by capturing them in Sentry and logging to the console.
   *
   * @param error - The error to handle.
   * @param message - Optional message to log with the error.
   * @returns void
   */
  const handleError = (error: unknown, message = "An error occurred"): void => {
    Sentry.captureException(error);
    console.warn(`ERROR: ${message}: ${error}`);
  };

  if (kwnumber && userpass) {
    kwInput.value = kwnumber;
    kwPasswordInput.value = userpass;
    userNameInput.value = username ?? "";
    setSentryUser(kwInput, userNameInput);
  }

  // Prevent spaces from being used in the username field
  userNameInput.addEventListener("input", function () {
    (this as HTMLInputElement).value = (this as HTMLInputElement).value.replace(/\s+/g, "");
  });

  // Build dependencies object for session functions
  const sessionDeps: SessionDependencies = {
    startButton,
    statusMessage,
    loginFieldset,
    kwInput,
    kwPasswordInput,
    userNameInput,
    terminal: null,
    serviceUrl: ApiEndpoints.FRAME_GQL,
  };

  const setTerminal = (newTerminal: FrameTerminal): void => {
    terminal = newTerminal;
    sessionDeps.terminal = newTerminal;
  };

  selectedLocationInput.addEventListener("change", async (event) => {
    const target = event.target as HTMLSelectElement;
    selectedLocation = target.value;
    setSentryLocation(target.options[target.selectedIndex]);
    try {
      const account = getEnumKeyByEnumValue(AwsRegionNames, target.value);
      const token = await getToken();
      await loadFrame(FrameAccountConfigs[account as string], token, sessionDeps, setTerminal);
    } catch (error) {
      handleError(error, "Error changing location");
    }
  });

  // Validate form
  loginForm.addEventListener("submit", async (event) => {
    event.preventDefault();

    // check form validity
    if (!loginForm.checkValidity()) {
      console.warn("User did not fill out all required fields");
      loginForm.reportValidity();
      return;
    }

    await validateCredentials();
    return;
  });

  // Clear browser cookies and local storage
  clearButton.addEventListener("click", async () => {
    try {
      clearCookie(CookieNames.KWNUMBER);
      clearCookie(CookieNames.PASSWORD);
      clearCookie(CookieNames.USERNAME);
      clearLocalStorage();
      statusMessage.innerText = "Login information cleared";
      loginForm.reset();
    } catch (error) {
      handleError(error, "Error clearing cookies");
      errorContainer.innerText = "Error clearing login. Please reload the page and try again.";
    }
  });

  /**
   * Validates the user's credentials and starts or resumes a Frame session.
   *
   * @returns A promise that resolves when the user's credentials are validated.
   * @throws {Error} If we cannot validate the users credentials.
   */
  const validateCredentials = async (): Promise<void> => {
    if (terminal) {
      const session = (await terminal.getOpenSession()) as Awaited<
        ReturnType<typeof terminal.getOpenSession>
      > | null;
      const sessionState = session?.state as unknown as string | undefined;
      if (session && sessionState === SessionState.OPEN) {
        await terminal.resume(session.id);
        toggleStartButton(startButton, true, "Resuming...");
      } else if (session && sessionState === SessionState.CLOSING) {
        // Wait for session to close before starting a new one
        toggleStartButton(startButton, false, "Waiting for session to close...");
        statusMessage.innerText = "Previous session is closing. Please wait...";
        const sessionClosed = await checkUntilClosed(sessionDeps);
        if (sessionClosed) {
          // Recursively call validateCredentials after session is closed
          await validateCredentials();
        }
      } else {
        try {
          const response = await axios.post(
            ApiEndpoints.LOGIN,
            `pLogin=${kwInput.value}&pPwd=${kwPasswordInput.value}`
          );
          const ologin = (response.data as { APIResult: number }).APIResult;
          if (ologin === 1) {
            errorContainer.textContent = "";
            setCookie(CookieNames.KWNUMBER, kwInput.value, 180);
            setCookie(CookieNames.PASSWORD, kwPasswordInput.value, 180);
            setCookie(CookieNames.USERNAME, userNameInput.value, 180);

            setSentryUser(kwInput, userNameInput);

            statusMessage.innerText = "Connection in progress";
            const account = getEnumKeyByEnumValue(AwsRegionNames, selectedLocation);
            if (account && FrameAccountConfigs[account]) {
              await startSession(FrameAccountConfigs[account].applicationId, sessionDeps);
            } else {
              console.warn("Unknown region selected in validateCredentials:", selectedLocation);
              errorContainer.innerText = "Unknown location selected. Please refresh and try again.";
            }
            await postScreenSpecs(kwInput);
          } else {
            errorContainer.innerText =
              "Your KW number and/or password were not valid, were missing, or your company / agency required that we disable cloud access.";
          }
        } catch (error) {
          handleError(error, "Error validating credentials");
        }
      }
    }
  };

  /**
   * Gets the region information from latency results for a given region ID.
   *
   * @param results - The latency results array.
   * @param region - The AWS region ID to find.
   * @returns The region information or undefined if not found.
   */
  const getRegionInfoFromResults = (
    results: LatencyResult[],
    region: string
  ): LatencyResult | undefined => {
    return results.find((r) => r.id === region);
  };

  /**
   * Find the lowest latency region and select it for the user.
   *
   * @param token - Frame token.
   * @returns void
   */
  const startApp = async (token: string): Promise<void> => {
    const latencyResults: Array<Promise<LatencyResult>> = [];

    // ping each region and push the results to latencyResults array
    Object.entries(AwsRegionNames).forEach(([, value]) => {
      const promise = pingRegion(value);
      if (promise) latencyResults.push(promise);
    });

    const results = await Promise.all(latencyResults);

    // sort the results by latency
    results.sort((a, b) => a.latency - b.latency);

    // Build the set of region IDs that are actually present in the <select>.
    // This is the canonical authoritative set — regions in AwsRegionNames but absent
    // from the <select> (NORCAL, SYDNEY) must not be auto-selected.
    const availableOptionValues = new Set(
      Array.from(selectedLocationInput.options)
        .filter((opt) => opt.value !== "" && !opt.disabled)
        .map((opt) => opt.value)
    );

    const regionWithLowestLatency = pickLowestLatencyAvailableRegion(
      results,
      availableOptionValues
    );

    if (!regionWithLowestLatency) {
      console.warn(
        "startApp: no selectable regions available in <select>; skipping auto-selection"
      );
      toggleStartButton(startButton, true, "Launch");
      return;
    }

    const options = Array.from(selectedLocationInput.options);

    // update the labels with the latency
    options.forEach((option) => {
      const regionInfo = getRegionInfoFromResults(results, option.value);
      if (regionInfo === undefined) return;
      if (regionWithLowestLatency.id === regionInfo.id) {
        option.selected = true;
      }
      option.label = `${option.dataset.name} (${regionInfo.latency} ms)`;
    });

    // start with the region with the lowest latency selected
    selectedLocation = regionWithLowestLatency.id;
    setSentryLocation(options.find((option) => option.value === selectedLocation));

    const account = getEnumKeyByEnumValue(AwsRegionNames, selectedLocation);
    await loadFrame(FrameAccountConfigs[account as string], token, sessionDeps, setTerminal);
    return;
  };

  /**
   * Detects the user's browser and provides browser-specific advisories.
   *
   * @returns True if the browser is supported, false otherwise.
   */
  const browserDetection = (): boolean => {
    if (isBrowserSupported()) {
      return true;
    }
    browserAdvisory.classList.remove("hidden");
    toggleStartButton(startButton, false, "Launch");
    return false;
  };

  // Add event listener to close button
  if (awsNotificationCloseBtn) {
    awsNotificationCloseBtn.addEventListener("click", () => {
      hideAwsNotification(awsNotificationBanner);

      // Store in localStorage that the user has dismissed this notification
      localStorage.setItem(StorageKeys.NOTIFICATION_DISMISSED, "true");
    });
  }

  showNotices(data as NoticesJson, awsNotificationBanner);
  updateYear(copyrightYear);
  browserDetection();
  let frameToken: string | null = null;
  try {
    frameToken = await getToken();
  } catch (error) {
    handleError(error, "Error getting token");
  }
  if (!frameToken) {
    errorContainer.textContent =
      "We're having trouble connecting to the cloud platform. Please reload the page and try again. If the problem persists, please contact support.";
    return;
  }
  try {
    await startApp(frameToken);
  } catch (error) {
    handleError(error, "Error starting app");
    errorContainer.textContent =
      "We're having trouble starting a cloud session. Please reload the page and try again. If the problem persists, please contact support.";
  }
});
